'use client'

import { Markdown } from '@/lib/ui-kit/components/Markdown.component'
import { ReeveButtonApiResponse } from '@/lib/api-connector-strapi-cms/api/reeve-landing-page/reeveLandingPageApi.types'
import LineTopLeft from '@/lib/ui-kit/assets/icons/reeve/header/reeve-header-line-top-left.svg'
import LineBottomLeft from '@/lib/ui-kit/assets/icons/reeve/header/reeve-header-line-bottom-left.svg'
import LineTopRight from '@/lib/ui-kit/assets/icons/reeve/header/reeve-header-line-top-right.svg'
import LineBottomRight from '@/lib/ui-kit/assets/icons/reeve/header/reeve-header-line-bottom-right.svg'
import { motion } from 'framer-motion'
import Graph from '@/lib/ui-kit/assets/icons/reeve/header/graph.svg'
import ChartSquare from '@/lib/ui-kit/assets/icons/reeve/header/chart-square.svg'
import ArrowUp from '@/lib/ui-kit/assets/icons/reeve/header/arrow-up.svg'
import Eye from '@/lib/ui-kit/assets/icons/reeve/header/eye.svg'
import ArrowRightSquare from '@/lib/ui-kit/assets/icons/reeve/header/arrow-right-square.svg'
import User from '@/lib/ui-kit/assets/icons/reeve/header/security-user.svg'
import People from '@/lib/ui-kit/assets/icons/reeve/header/people.svg'
import ArrowRight from '@/lib/ui-kit/assets/icons/reeve/header/arrow-right.svg'
import ArrowDown from '@/lib/ui-kit/assets/icons/reeve/header/arrow-down.svg'
import RandomColorButton from '@/modules/reeve/components/RandomColorButton.component'
import { AnimatedComplexLine } from '@/modules/reeve/components/AnimatedComplexLine.component'

interface HeroProps {
  heading: string
  subHeading: string
  button: ReeveButtonApiResponse
}

export const Hero = ({ heading, subHeading, button }: HeroProps) => {
  return (
    <section className="2xl:mask-horizontal relative m-auto flex h-[36rem] max-w-[1440px] items-center justify-center overflow-hidden px-6 pt-16 sm:h-[48rem] xl:mt-[-76px] xl:h-[84.25rem] xl:px-10 xl:pt-10 2xl:pt-0">
      <AnimatedComplexLine
        component={LineTopLeft}
        className="absolute -left-40 -top-[13rem] sm:-left-20 sm:-top-[8rem] xl:-left-20 xl:top-20 2xl:-left-20"
        delay={0.1}
      />
      <AnimatedComplexLine
        component={LineBottomLeft}
        className="absolute top-1/2 hidden -translate-y-1/2 2xl:-left-20 2xl:block"
        delay={0.3}
      />
      <AnimatedComplexLine
        component={LineTopRight}
        className="absolute top-48 hidden 2xl:-right-20 2xl:block"
        delay={0.5}
      />
      <AnimatedComplexLine
        component={LineBottomRight}
        className="absolute -bottom-[16.5rem] -right-[24rem] block sm:-bottom-40 sm:-right-80 xl:-right-64 xl:bottom-48 2xl:-right-40"
        delay={0.7}
      />

      {/*Left icons*/}
      <motion.div
        className="absolute top-[13.75rem] hidden rounded-lg bg-reeve-yellow p-2 xl:left-[19.25rem] xl:block"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          delay: 1,
          duration: 0.5,
          type: 'spring',
          stiffness: 260,
          damping: 20,
        }}
      >
        <Graph className="h-7 w-7 fill-reeve-primary" />
      </motion.div>

      <motion.div
        className="absolute left-[9.25rem] top-[7.9rem] block rounded-full bg-reeve-green p-2 sm:left-[14.4rem] sm:top-[8.6rem] xl:left-[14.45rem] xl:top-[19.7rem] 2xl:left-[6.35rem] 2xl:top-[26.1rem]"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          delay: 1.1,
          duration: 0.5,
          type: 'spring',
          stiffness: 260,
          damping: 20,
        }}
      >
        <ChartSquare className="h-7 w-7 fill-reeve-primary" />
      </motion.div>

      <motion.div
        className="2xl:right absolute top-[33rem] hidden flex-col gap-y-3 rounded-lg bg-reeve-orange p-2 sm:right-[9rem] sm:top-[38rem] sm:flex xl:hidden 2xl:left-[6.35rem] 2xl:right-auto 2xl:flex"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          delay: 1.2,
          duration: 0.5,
          type: 'spring',
          stiffness: 260,
          damping: 20,
        }}
      >
        <ArrowUp className="h-7 w-7 fill-reeve-primary" />
        <Eye className="h-7 w-7 fill-reeve-primary" />
        <ArrowRightSquare className="h-7 w-7 fill-reeve-primary" />
      </motion.div>

      <motion.div
        className="absolute hidden rounded-lg bg-reeve-purple px-3 py-2 text-[1.25rem] font-normal leading-[1.75rem] text-reeve-primary sm:left-[3.5rem] sm:top-[13.25rem] sm:block xl:top-[26.15rem] 2xl:left-[3.3rem] 2xl:top-[52.5rem]"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          delay: 1.3,
          duration: 0.5,
          type: 'spring',
          stiffness: 260,
          damping: 20,
        }}
      >
        Transparency
      </motion.div>

      {/*Right icons*/}
      <motion.div
        className="absolute hidden rounded-lg bg-reeve-purple p-2 xl:right-[13.05rem] xl:top-[55.6rem] xl:block 2xl:right-[1.75rem] 2xl:top-[16.5rem]"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          delay: 1.4,
          duration: 0.5,
          type: 'spring',
          stiffness: 260,
          damping: 20,
        }}
      >
        <User className="h-7 w-7 fill-reeve-primary" />
      </motion.div>

      <motion.div
        className="absolute right-[5rem] top-[28.55rem] block rounded-full bg-reeve-yellow p-2 sm:right-[3.5rem] sm:top-[33.8rem] sm:block xl:hidden 2xl:right-[1.75rem] 2xl:top-[23rem] 2xl:block"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          delay: 1.5,
          duration: 0.5,
          type: 'spring',
          stiffness: 260,
          damping: 20,
        }}
      >
        <People className="h-7 w-7 fill-reeve-primary" />
      </motion.div>

      <motion.div
        className="absolute top-[48.1rem] hidden rounded-lg bg-reeve-blue px-3 py-2 text-[1.25rem] font-normal leading-[1.75rem] text-reeve-primary xl:right-[5rem] xl:block 2xl:right-[12rem]"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          delay: 1.6,
          duration: 0.5,
          type: 'spring',
          stiffness: 260,
          damping: 20,
        }}
      >
        Innovation
      </motion.div>

      <motion.div
        className="absolute top-[48.1rem] hidden rounded-lg bg-reeve-orange p-2 xl:right-[1.75rem] xl:block 2xl:right-[8.75rem]"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          delay: 1.7,
          duration: 0.5,
          type: 'spring',
          stiffness: 260,
          damping: 20,
        }}
      >
        <ArrowRight className="h-7 w-7 fill-reeve-primary" />
      </motion.div>

      <motion.div
        className="absolute hidden rounded-lg bg-reeve-green p-2 2xl:right-[0.5rem] 2xl:top-[70.8rem] 2xl:block"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          delay: 1.8,
          duration: 0.5,
          type: 'spring',
          stiffness: 260,
          damping: 20,
        }}
      >
        <ArrowDown className="h-7 w-7 fill-reeve-primary" />
      </motion.div>

      <div className="flex max-w-4xl flex-col items-start justify-center sm:items-center xl:-mt-32">
        <motion.h1
          className="heading-xl text-left font-extrabold text-reeve-primary sm:text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Markdown>{heading}</Markdown>
        </motion.h1>
        <motion.div
          className="pt-3 text-[1.125rem] leading-[1.7rem] text-reeve-secondary-gray sm:pt-4 sm:text-center sm:text-[1.25rem] sm:leading-[1.5rem] xl:text-[1.5rem] xl:leading-[2rem]"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <Markdown>{subHeading}</Markdown>
        </motion.div>
        <motion.div
          className="pt-6 sm:pt-6 xl:pt-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <RandomColorButton
            type={button.type}
            link={button.link}
            label={button.label}
          />
        </motion.div>
      </div>
    </section>
  )
}
